import type { PersonaProfile } from '../engine/types';

/**
 * §10.3 — classification before options, for both persona and objection.
 * Non-negotiable: if the response options are on screen first, the rep
 * reverse-engineers the answer from them and the measurement is worthless.
 *
 * Which is why this renders on its own, with nothing else to read.
 */
export function Chooser({
  prompt, hint, choices, onChoose, footer,
}: {
  prompt: string;
  hint?: string;
  choices: { id: string; label: string; sub?: string }[];
  onChoose: (id: string) => void;
  footer?: React.ReactNode;
}) {
  return (
    <section className="chooser" aria-live="polite">
      <h2 className="chooser-prompt">{prompt}</h2>
      {hint && <p className="muted small">{hint}</p>}
      <ul className="chooser-list">
        {choices.map((c) => (
          <li key={c.id}>
            <button className="chooser-choice" onClick={() => onChoose(c.id)}>
              <span className="chooser-label">{c.label}</span>
              {c.sub && <span className="chooser-sub small muted">{c.sub}</span>}
            </button>
          </li>
        ))}
      </ul>
      {footer}
    </section>
  );
}

export const personaChoices = (personas: PersonaProfile[], ids: string[]) =>
  ids
    .map((id) => personas.find((p) => p.id === id))
    .filter((p): p is PersonaProfile => !!p)
    .map((p) => ({ id: p.id, label: p.name, sub: p.soundsLike }));

const CATEGORY_LABEL: Record<string, string> = {
  money: 'Money', time: 'Time', trust: 'Trust',
  comprehension: "They don't understand it", authority: "It's not their decision alone",
  not_interested: 'Not interested',
};

export const objectionChoices = (ids: string[]) =>
  ids.map((id) => ({ id, label: CATEGORY_LABEL[id] ?? id.replace(/_/g, ' ') }));
